/**
 * @file 時間配分の計画スナップショット (StudySchedule) を取得する hook。
 *
 * @what: familyId ごとに 1 回読み、`ready` では未同期 (404) を `schedule: null` で表す。
 * @why: 読み取り専用の参照情報なので、plan の draft 機構とは独立に持つ (exploration と同型)。
 * @failure: 404 以外の失敗は `error` (画面は控えめな 1 行で知らせる。詳細は console のみ)。
 */

import { useEffect, useState } from "react";
import { fetchStudySchedule, type StudySchedule } from "../api/schedule";

export type StudyScheduleState =
  | { status: "loading" }
  | { status: "ready"; schedule: StudySchedule | null }
  | { status: "error" };

export function useStudySchedule(familyId: string): StudyScheduleState {
  const [state, setState] = useState<StudyScheduleState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchStudySchedule(familyId, controller.signal)
      .then((schedule) => {
        if (!controller.signal.aborted) {
          setState({ status: "ready", schedule });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load schedule", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId]);

  return state;
}
